import {
    type Camera,
    CineonToneMapping,
    LinearSRGBColorSpace,
    PCFSoftShadowMap,
    type Scene,
    WebGLRenderer,
} from "three";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import type Sizes from "./utils/Sizes";

export default class Renderer {
    readonly #renderer: WebGLRenderer;

    constructor(canvas: HTMLCanvasElement, sizes: Sizes) {
        this.#renderer = new WebGLRenderer({
            canvas,
            antialias: true,
            powerPreference: "high-performance",
        });
        this.#renderer.outputColorSpace = LinearSRGBColorSpace;
        this.#renderer.toneMapping = CineonToneMapping;
        this.#renderer.toneMappingExposure = 1.75;
        this.#renderer.shadowMap.enabled = true;
        this.#renderer.shadowMap.type = PCFSoftShadowMap;
        this.#renderer.setClearColor("#211d20");
        this.#renderer.setPixelRatio(sizes.pixelRatio);
        this.#renderer.setSize(sizes.width, sizes.height);
    }

    get instance() {
        return this.#renderer;
    }

    getSnapShot() {
        // TODO: Create 350x350 canvas in the background and re-initialize the scene within (should have default settings)
        return this.#renderer.domElement.toDataURL("image/png");
    }

    resize = (sizes: Sizes): void => {
        this.#renderer.setPixelRatio(sizes.pixelRatio);
        this.#renderer.setSize(sizes.width, sizes.height);
    };

    renderFrame = (scene: Scene, camera: Camera): void => {
        this.#renderer.render(scene, camera);
    };

    render = (scene: Scene, camera: Camera, deltaTime: number): void => {
        const start = performance.now();
        this.renderFrame(scene, camera);
        const frameTime = performance.now() - start;

        const { render, memory } = this.#renderer.info;

        DiagnosticsStore.collectPerformanceSample({
            deltaTime,
            frameTime,
            drawCalls: render.calls,
            triangles: render.triangles,
            geometries: memory.geometries,
            textures: memory.textures,
        });
    };

    dispose = () => {
        this.#renderer.dispose();
    };
}
